import React, { useContext, useState, useEffect } from 'react'
import cookies from 'js-cookie'
import { Variation, Experiment } from '@deal/experiment-js'
import { AnalyticsContext } from '#src/containers/Analytics'
import { IdentityContext } from '#src/containers/Identity'
import config from '#src/config'
import { SellableForModalComponent } from '#src/generated/graphql'
import logger from '#src/services/logger'
import { EngagementChannelCategoryAssetsComponent } from '#src/generated/graphql'
import V1 from './v1'
import V2 from './v2'

export enum Stage {
  Initial,
  Typing,
  Success
}

interface SmsModalProps {
  sellableId?: string
  categoryId: string
}

const SMSModal: React.SFC<SmsModalProps> = ({ sellableId, categoryId }) => {
  if (!sellableId) {
    logger.captureException('Attempted to render sms modal without a sellable id')
    return null
  }

  const { myself } = useContext(IdentityContext)
  const analytics = useContext(AnalyticsContext)

  const [currentStage, setCurrentStage] = useState<Stage>(Stage.Initial)
  const [isModalOpen, setIsModalOpen] = useState(true)

  /**
   * If the user is logged in, check to see if we should hide the modal or not
   */
  if (myself) {
    const modalCriteria = [
      () => {
        // Always hide the modal if they've viewed it before
        return cookies.get('sms_modal_viewed') !== undefined
      },
      () => {
        // If they're logged in and the stage isn't the success stage, hide the modal
        return myself && currentStage !== Stage.Success ? true : false
      },
      () => {
        // If they have a recent lead, don't continue to show them this treatment
        return myself && myself.user.mostRecentLead && myself.user.mostRecentLead.id !== undefined
          ? true
          : false
      }
    ]

    if (modalCriteria.every(criteria => !!criteria())) {
      return null
    }
  }

  // If user is authenticated, only show modal if they have just registered
  useEffect(() => {
    if (cookies.get('sms_modal_registered')) {
      setCurrentStage(Stage.Success)
    }
  }, [myself])

  const handleModalToggleState = (isOpen: boolean) => {
    if (!isOpen) {
      cookies.set('sms_modal_viewed', 'true', {
        expires: 1,
        domain: config.get('consumer.clientSideCookieDomain')
      })
    }

    setIsModalOpen(isOpen)
  }

  return (
    <SellableForModalComponent
      variables={{
        friendlyIdOrId: sellableId
      }}
    >
      {({ data }) => {
        if (data && data.findSellable) {
          const sellable = data.findSellable

          return (
            <EngagementChannelCategoryAssetsComponent
              variables={{
                categoryId
              }}
            >
              {({ data }) => {
                if (data && data.category) {
                  return (
                    <Experiment
                      id="sms-modal"
                      default="v1"
                      options={{
                        suppressTracking: myself !== undefined
                      }}
                    >
                      <Variation id="v1">
                        <V1
                          category={data.category}
                          isModalOpen={isModalOpen}
                          stage={currentStage}
                          setIsModalOpen={handleModalToggleState}
                          handleStage={(stage: Stage) => setCurrentStage(stage)}
                          onDismissClick={(variation: string, categorySlug: string) => {
                            analytics?.events.engagementChannelDismissed({
                              engagement_channel: 'sms-modal',
                              variation,
                              category: categorySlug
                            })

                            setIsModalOpen(false)
                          }}
                        />
                      </Variation>
                      <Variation id="v2">
                        <V2
                          sellable={sellable}
                          category={data.category}
                          isModalOpen={isModalOpen}
                          stage={currentStage}
                          setIsModalOpen={handleModalToggleState}
                          handleStage={(stage: Stage) => setCurrentStage(stage)}
                          onDismissClick={(variation: string, categorySlug: string) => {
                            analytics?.events.engagementChannelDismissed({
                              engagement_channel: 'sms-modal',
                              variation,
                              category: categorySlug
                            })

                            setIsModalOpen(false)
                          }}
                        />
                      </Variation>
                    </Experiment>
                  )
                }
                return null
              }}
            </EngagementChannelCategoryAssetsComponent>
          )
        }

        return null
      }}
    </SellableForModalComponent>
  )
}

export default SMSModal
